/**
 * Intake Channels: settings page showing the connection status of all
 * four official channels (Google Sheets / Messenger / WhatsApp / SMS).
 *
 * Each card reflects the platform-level config (env flags) AND the
 * per-business connection state. When the platform is not configured we
 * render a "Not enabled yet" empty state with a link to the setup doc;
 * this is a deliberate honest-by-default behaviour so owners never see
 * a fake "connected" message.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  FileSpreadsheet, MessageSquareText, MessageCircle, Smartphone,
  ShieldCheck, AlertCircle, ArrowRight, Loader2, ExternalLink, Unplug,
} from "lucide-react";
import WebhooksCard from "@/components/intake_channels/WebhooksCard";

const CHANNEL_META = {
  google_sheets: {
    title: "Google Sheets",
    blurb: "Sync booking rows from your Google account. Read-only access; tokens encrypted at rest. Imported rows become drafts you still review.",
    Icon: FileSpreadsheet,
    accent: "emerald",
  },
  messenger: {
    title: "Messenger / Facebook Page",
    blurb: "Receive Page messages directly into ConfirmPH and reply with the confirmation link through the official Meta API. No scraping, no personal logins.",
    Icon: MessageSquareText,
    accent: "indigo",
  },
  whatsapp: {
    title: "WhatsApp Business Cloud",
    blurb: "Send approved confirmation-template messages through your WhatsApp Business number and receive customer replies into your draft queue.",
    Icon: MessageCircle,
    accent: "green",
  },
  sms: {
    title: "SMS",
    blurb: "Send the confirmation link via Semaphore (Philippines) or Twilio (global). The link itself is the same: staff can still copy/resend manually anytime.",
    Icon: Smartphone,
    accent: "sky",
  },
};

export default function IntakeChannelsSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/intake/channels/overview");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="intake-channels-page">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">
          Official intake channels
        </h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          Connect ConfirmPH to the channels your customers already use.
          Every channel creates a <strong>draft for staff review</strong>: we
          never auto-confirm bookings from incoming messages, and the
          manual Copy-message flow always stays available.
        </p>
      </header>

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading channels…
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {["google_sheets", "messenger", "whatsapp", "sms"].map((k) => (
            <ChannelCard
              key={k}
              channel={k}
              platform={data.platform[k] || {}}
              company={data.company[k] || {}}
              onChanged={load}
            />
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Setting up the platform-level credentials?{" "}
        <Link to="/docs/INTAKE_CHANNELS_SETUP.md"
               className="text-[#1B4332] underline font-medium"
               data-testid="intake-channels-setup-doc">
          Read the setup checklist
        </Link>{" "}- covers Google Cloud Console, Meta App review, the
        WhatsApp Cloud API template, and the SMS provider keys.
      </div>

      <WebhooksCard />
    </div>
  );
}

function ChannelCard({ channel, platform, company, onChanged }) {
  const meta = CHANNEL_META[channel];
  const enabled = !!platform.enabled;
  const platformConfigured = enabled && (
    platform.client_configured ||
    platform.app_configured ||
    platform.platform_configured ||
    platform.provider_configured
  );
  const connected = !!company.refresh_token ||
                     !!company.page_access_token ||
                     !!company.access_token ||
                     (channel === "sms" && platformConfigured);
  const Icon = meta.Icon;

  return (
    <div className={`rounded-xl border bg-white p-5 ${
                       connected ? "border-emerald-300" : "border-slate-200"
                     }`}
         data-testid={`intake-channel-card-${channel}`}>
      <header className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold">{meta.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{meta.blurb}</p>
        </div>
      </header>

      <div className="mt-4 text-xs space-y-1.5" data-testid={`intake-channel-status-${channel}`}>
        <StatusRow label="Platform enabled" ok={enabled} />
        <StatusRow label="Provider credentials" ok={platformConfigured}
                    helper={!enabled ? "Set the env flag first" :
                              "Add the keys from the setup doc"} />
        {channel === "sms" && (
          <StatusRow label="Active provider"
                      ok={!!platform.provider_configured}
                      helper={platform.provider || "Pick semaphore or twilio"} />
        )}
        {channel === "whatsapp" && platform.template && (
          <StatusRow label="Approved template"
                      ok={!!platform.platform_configured}
                      helper={platform.template} />
        )}
        <StatusRow label="This business connected" ok={connected} />
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
        {enabled && platformConfigured ? (
          <ChannelActions channel={channel} connected={connected}
                            company={company} onChanged={onChanged} />
        ) : (
          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Not enabled on this platform yet
          </span>
        )}
        <Link to="/docs/INTAKE_CHANNELS_SETUP.md"
               className="text-xs text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1">
          Setup doc <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function StatusRow({ label, ok, helper }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-600">{label}</span>
      <span className={`inline-flex items-center gap-1 font-medium ${
                          ok ? "text-emerald-700" : "text-slate-400"
                        }`}>
        {ok ? <ShieldCheck className="w-3.5 h-3.5" />
            : <AlertCircle className="w-3.5 h-3.5" />}
        {ok ? "Ready" : (helper || "Not yet")}
      </span>
    </div>
  );
}

function ChannelActions({ channel, connected, company, onChanged }) {
  const [busy, setBusy] = useState(false);

  const connectGoogle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/intake/google-sheets/oauth/start");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect this channel? Stored tokens will be wiped.")) return;
    setBusy(true);
    try {
      const map = {
        google_sheets: "/intake/google-sheets/disconnect",
        messenger: "/intake/messenger/disconnect",
        whatsapp: "/intake/whatsapp/disconnect",
      };
      const path = map[channel];
      if (!path) {
        toast.info("SMS is a platform-level provider: nothing to disconnect per-business.");
        return;
      }
      await api.post(path);
      toast.success("Disconnected.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (channel === "google_sheets") {
    return connected ? (
      <button onClick={disconnect} disabled={busy}
              data-testid={`intake-channel-disconnect-${channel}`}
              className="inline-flex items-center gap-1 text-sm text-rose-700 hover:underline">
        <Unplug className="w-3.5 h-3.5" /> Disconnect
        {company.account_email && <span className="text-slate-500 ml-1">({company.account_email})</span>}
      </button>
    ) : (
      <button onClick={connectGoogle} disabled={busy}
              data-testid={`intake-channel-connect-${channel}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-brand text-sm font-medium">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
        Connect Google
      </button>
    );
  }

  if (channel === "sms") {
    return (
      <span className="text-xs text-emerald-700 inline-flex items-center gap-1"
             data-testid="intake-channel-sms-ready">
        <ShieldCheck className="w-3.5 h-3.5" /> Send via SMS available in draft delivery panel.
      </span>
    );
  }

  // Messenger / WhatsApp: per-business connect lives inline in the
  // settings page for the scaffold. UI wired up in a follow-up.
  return connected ? (
    <button onClick={disconnect} disabled={busy}
            data-testid={`intake-channel-disconnect-${channel}`}
            className="inline-flex items-center gap-1 text-sm text-rose-700 hover:underline">
      <Unplug className="w-3.5 h-3.5" /> Disconnect
    </button>
  ) : (
    <span className="text-xs text-slate-500" data-testid={`intake-channel-pending-${channel}`}>
      Awaiting Meta app review: see setup doc for required permissions.
    </span>
  );
}
